<template>
  <div class="badge-container">
    <!--
      @slot content for any other component that needs a badge
    -->
    <slot />
    <span
      class="badge"
      :class="[
        hidden ? 'is-hidden' : '',
        size,
        horizontalPosition,
        verticalPosition,
        color
      ]"
      :aria-label="ariaLabel"
      :style="badgePosition"
    >
      {{ content }}
    </span>
  </div>
</template>

<script>
export default {
  name: 'Badge',
  props: {
    // /** Applies `primary` by default; Color choices are based on Bulma's color naming e.g. Primary, White etc. */
    color: {
      type: String,
      default: 'badge-primary',
      validator: function (value) {
        return (
          ['badge-primary', 'badge-white', 'badge-black'].indexOf(value) !== -1
        )
      }
    },
    /** Any content you want injected as text into the badge. */
    content: {
      type: String
    },
    // /** Reduce the size of the badge. e.g. default, compact, dot */
    size: {
      type: String,
      required: false,
      default: 'default',
      validator: function (value) {
        return ['default', 'compact', 'dot'].indexOf(value) !== -1
      }
    },
    /** The aria-label used for the badge. Use it when dot is enabled */
    ariaLabel: {
      type: String
    },
    /** Applies 'left' by default. Aligns the component `left` or `right` */
    horizontalPosition: {
      type: String,
      default: 'left',
      validator: function (value) {
        return ['left', 'right'].indexOf(value) !== -1
      }
    },
    /** Applies 'top' by default. Aligns the component `top` or `bottom` */
    verticalPosition: {
      type: String,
      default: 'top',
      validator: function (value) {
        return ['top', 'bottom'].indexOf(value) !== -1
      }
    },
    offsetX: {
      type: String
    },
    offsetY: {
      type: String
    },
    /** Controls whether the component is visible or hidden. */
    hidden: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    badgePosition: function () {
      return Object.assign(this.xAxisPosition(), this.yAxisPosition())
    }
  },
  methods: {
    xAxisPosition: function () {
      let position = {}
      if (this.offsetX) {
        position[this.horizontalPosition] = this.offsetX
      }
      return position
    },
    yAxisPosition: function () {
      let position = {}
      if (this.offsetY) {
        position[this.verticalPosition] = this.offsetY
      }
      return position
    }
  }
}
</script>

<style lang="scss" scoped>
@mixin badge-variant($color, $background, $borderColor, $opacity) {
  color: $color;
  background-color: $background;
  border: 1px solid $borderColor;

  &:hover,
  &:active,
  &:focus {
    color: $color;
    opacity: $opacity;
    border-color: $borderColor;
    box-shadow: none !important;
  }
}

$position: -10px;
$compact-position: -7px;
$dot-position: -4px;

.badge-container {
  position: relative;
  display: inline-block;
}

.badge-container > .badge {
  position: absolute;
  border-radius: 50%;
}

.badge {
  padding: 2px 4px;
  min-width: 25px;
  text-align: center;
}

.top {
  top: $position;
  bottom: auto;
}

.bottom {
  bottom: $position;
  top: auto;
}

.right {
  right: $position;
  left: auto;
}

.left {
  left: $position;
  right: auto;
}

.compact {
  padding: 1px 2px;
  min-width: 15px;
  height: 15px;
  text-align: center;
  font-size: 9px;
  font-weight: 700;

  &.top {
    top: $compact-position;
    bottom: auto;
  }

  &.bottom {
    bottom: $compact-position;
    top: auto;
  }

  &.right {
    right: $compact-position;
    left: auto;
  }

  &.left {
    left: $compact-position;
    right: auto;
  }
}

.dot {
  text-indent: 1000%;
  white-space: nowrap;
  overflow: hidden;
  height: 10px;
  width: 10px;
  min-width: 10px;
  top: auto;
  bottom: auto;
  right: auto;
  left: auto;

  &.top {
    top: $dot-position;
    bottom: auto;
  }

  &.bottom {
    bottom: $dot-position;
    top: auto;
  }

  &.right {
    right: $dot-position;
    left: auto;
  }

  &.left {
    left: $dot-position;
    right: auto;
  }
}

.badge-primary {
  @include badge-variant($white, $primary, $primary, 0.8);
}

.badge-white {
  @include badge-variant($black, $white, $black, 0.7);
}

.badge-black {
  @include badge-variant($white, $black, $black, 0.8);
}
</style>
